import { css } from '@emotion/core'
import { trackEvent } from '@fs/zion-analytics'
import { Arrow, DocumentBox, ContentAttach } from '@fs/zion-icon'
import { SaveToSourceBox } from '@fs/zion-record-details'
import { useHistory, useQueryParams } from '@fs/zion-router'
import {
  Breadcrumbs,
  Cell,
  Ellipsis,
  H1,
  IconButton,
  ListItem,
  MarginBox,
  MenuOverlay,
  Row,
  useMenuOverlay,
  usePageWidth,
} from '@fs/zion-ui'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Title } from '@fs/zion-head'
import { useOverlayContext } from '../../providers/overlayContextProvider/OverlayContext'
import NavButtons from './NavButtons'
import { useSharedBrowserStateContext } from '../../providers/sharedBrowserProvider/SharedBrowserContext'
import useNamesExperienceOptIn from '../../../RIP/hooks/useNamesExperienceOptIn'

const bringToFrontStyle = css`
  z-index: 1000;
  position: relative;
`

const HeaderNavigation = ({ showNavButtons = true, breadcrumbs = [], title = '', showBreadcrumbs = true }) => {
  const [t] = useTranslation()
  const { query } = useQueryParams({ replaceState: true })
  const sharedBrowserState = useSharedBrowserStateContext()
  const { openOverlay } = useOverlayContext()
  const { isOptedIn } = useNamesExperienceOptIn()
  const pageWidth = usePageWidth()
  const isMobile = pageWidth({ default: true, md: false })
  const isFullText = query.view === 'fullText'
  const history = useHistory()

  const handleBackClick = useCallback((e) => {
    e.preventDefault()
    window.history.back()
  }, [])

  const saveRecordOverlay = useMenuOverlay()

  const handleAttachToTree = () => {
    trackEvent({
      event_name: 'click_action',
      link_name: 'Attach to Tree Start',
      page_context: query.view,
    })

    const err = sharedBrowserState.imageError
    const imageStatusRestricted = err?.status !== 'OK' && err?.status !== 'UNAVAILABLE'

    if (!err || !imageStatusRestricted) {
      // FSS-14672: always go straight to the classic 4-step Attach to Tree flow -
      // the indexed-names list panel is removed and no longer used in any state.
      openOverlay('mainInfosheet')
      openOverlay('attachToTreeOverlay')
    }
  }

  return (
    <MarginBox marginY="xs" marginX="sm">
      <Title>{title}</Title>
      <div>
        <Row alignY="middle">
          <Row>
            {!isFullText && (
              <IconButton
                Icon={Arrow}
                iconDirection="backward"
                color="gray"
                onClick={handleBackClick}
                aria-label={t('headerNavigation.goBack', 'Go back')}
                data-testid="header-back-button"
              />
            )}

            {/* Title and breadcrumbs column */}
            <Cell alignY="middle">
              <H1 size="H5">
                <Ellipsis>{title}</Ellipsis>
              </H1>
              {!isMobile && showBreadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} />}
            </Cell>
          </Row>
          {/* All right-side buttons */}
          <div growX />
          <NavButtons saveRecordOverlay={saveRecordOverlay} showNavButtons={showNavButtons} noShrinkX />
        </Row>
      </div>
      <div css={bringToFrontStyle}>
        <MenuOverlay {...saveRecordOverlay}>
          <ListItem
            data-testid="attach-to-tree-option"
            primaryText={t('headerNavigation.attachToTree', 'Attach to person in Family Tree')}
            linkName={isOptedIn ? 'fullTextAttachFlowV1-start-attach-to-tree' : 'start-attach-to-tree'}
            onClick={handleAttachToTree}
            startElement={<ListItem.Icon Icon={ContentAttach} />}
          />
          <SaveToSourceBox
            data-testid="save-image-to-source-box"
            primaryText={t('headerNavigation.saveToSourceBox', 'Save to source box')}
            defaultTitle={title}
            startElement={<ListItem.Icon Icon={DocumentBox} />}
            ark={`https://www.familysearch.org/ark:/61903/${history.location.pathname.split('/').pop()}`}
          />
        </MenuOverlay>
      </div>
    </MarginBox>
  )
}

export default HeaderNavigation
